import { useEffect, useState } from "react";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Download } from "lucide-react";
import { downloadExport } from "../operations/shared/downloadExport";
import { SiteFilterSelect } from "../operations/shared/SiteFilterSelect";
import { PaginationBar } from "@/components/shared/PaginationBar";

function defaultFrom() {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 10);
}
function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function CompletedPilesTab() {
  const [sites, setSites] = useState([]);
  const [site, setSite] = useState("");
  const [dateFrom, setDateFrom] = useState(defaultFrom());
  const [dateTo, setDateTo] = useState(today());
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    client.get("/dataflow/pile-export/sites").then((res) => setSites(res.data)).catch(() => {});
  }, []);

  const load = (pageNum = 1) => {
    setLoading(true);
    client
      .get("/dataflow/pile-export", { params: { site: site || undefined, date_from: dateFrom, date_to: dateTo, page: pageNum, page_size: 50 } })
      .then((res) => {
        setResult(res.data);
        setPage(res.data.page);
        setTotalPages(res.data.total_pages);
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  const exportXlsx = async () => {
    try {
      await downloadExport("/dataflow/pile-export/export", { site: site || undefined, date_from: dateFrom, date_to: dateTo });
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="completed-piles-tab">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1">Site</p>
          <SiteFilterSelect sites={sites} value={site} onChange={setSite} testId="completed-piles-site-filter" placeholder="All sites" allowAll />
        </div>
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1">From</p>
          <Input type="date" data-testid="completed-piles-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-44" />
        </div>
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1">To</p>
          <Input type="date" data-testid="completed-piles-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-44" />
        </div>
        <Button onClick={() => load(1)} disabled={loading} data-testid="completed-piles-load" className="bg-amber-600 hover:bg-amber-700">
          {loading ? "Loading..." : "Load"}
        </Button>
        <Button variant="outline" onClick={exportXlsx} data-testid="completed-piles-export">
          <Download className="h-4 w-4 mr-1.5" /> Export Excel
        </Button>
      </div>

      {result && (
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <div className="px-4 py-2 border-b border-slate-200 text-sm text-slate-500" data-testid="completed-piles-count">
            {result.count} completed pile{result.count === 1 ? "" : "s"} found
          </div>
          <Table data-testid="completed-piles-table">
            <TableHeader>
              <TableRow>
                <TableHead>Site</TableHead>
                <TableHead>Pile ID</TableHead>
                <TableHead>Machine</TableHead>
                <TableHead>Diameter (mm)</TableHead>
                <TableHead>Achieved Depth (m)</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Entered By</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {result.piles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                    No completed piles in this range.
                  </TableCell>
                </TableRow>
              ) : (
                result.piles.map((p) => (
                  <TableRow key={p.id} data-testid={`completed-pile-row-${p.id}`}>
                    <TableCell className="text-sm">{p.site_display_label}</TableCell>
                    <TableCell className="font-mono text-sm">{p.pile_id_display}</TableCell>
                    <TableCell className="text-sm">{p.machine_label}</TableCell>
                    <TableCell className="font-mono">{p.diameter_mm ?? "—"}</TableCell>
                    <TableCell className="font-mono">{p.achieved_depth_m ?? "—"}</TableCell>
                    <TableCell className="text-xs">{p.date ? new Date(p.date).toLocaleDateString("en-IN") : "—"}</TableCell>
                    <TableCell className="text-sm">
                      {p.created_by_name || "—"}
                      {p.entered_by_office && (
                        <Badge
                          variant="outline"
                          className="ml-2 text-[10px] px-1.5 py-0 border-sky-300 text-sky-700 bg-sky-50"
                          data-testid={`completed-pile-office-badge-${p.id}`}
                        >
                          Office
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
      <PaginationBar page={page} totalPages={totalPages} onPageChange={load} testId="completed-piles-pagination" />
    </div>
  );
}
